/** Featured course hero card — large spotlight card for a highlighted course with gradient background. */
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import type { Course } from '@formency/shared';
import { Tag } from '@/interfaces/learner/ui';

const EASE = [0.16, 1, 0.3, 1] as const;

interface FeaturedCourseProps {
  course: Course;
}

export default function FeaturedCourse({ course }: FeaturedCourseProps) {
  const categories = course.categories ?? [];
  const accent = course.accentColour;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
    >
      <Link
        to={`/courses/${course.id}`}
        className="group relative block overflow-hidden rounded-[28px]"
        style={{
          background: 'var(--color-surface-solid)',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {/* Header image or gradient backdrop */}
        {course.headerImageUrl ? (
          <div className="relative overflow-hidden" style={{ height: 'clamp(220px, 30vw, 420px)' }}>
            <img
              src={course.headerImageUrl}
              alt=""
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(to top, var(--color-surface-solid) 0%, rgba(0,0,0,0.1) 40%, transparent 70%)',
              }}
            />
          </div>
        ) : (
          <div
            className="h-3"
            style={{
              background: accent
                ? `linear-gradient(90deg, ${accent}40 0%, ${accent}15 100%)`
                : 'var(--color-brand-gradient)',
              opacity: accent ? 1 : 0.2,
            }}
          />
        )}

        {/* Content */}
        <div className="relative px-6 pb-6 pt-4 learner-single:px-8 learner-single:pb-8">
          {/* Categories */}
          {categories.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {categories.map((cat) => (
                <Tag key={cat.id} tone="accent">
                  {cat.name}
                </Tag>
              ))}
            </div>
          )}

          {/* Title */}
          <div className="flex items-start gap-4">
            {course.iconUrl && (
              <img
                src={course.iconUrl}
                alt=""
                className="mt-1 h-10 w-10 shrink-0 rounded-xl object-cover"
                style={{
                  border: '1px solid var(--color-border)',
                }}
              />
            )}
            <div className="min-w-0 flex-1">
              <h2
                className="text-section-title line-clamp-2"
                style={{
                  margin: 0,
                }}
              >
                {course.title}
              </h2>
              {course.description && (
                <p className="text-body mt-2 line-clamp-2" style={{ marginBottom: 0 }}>
                  {course.description}
                </p>
              )}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-5 flex items-center">
            <span
              className="inline-flex items-center gap-2 font-semibold transition-colors"
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-accent)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              View course
              <ArrowRight
                size={16}
                className="transition-transform duration-200 group-hover:translate-x-1"
                style={{ willChange: 'transform' }}
              />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

export function FeaturedCourseSkeleton() {
  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{
        background: 'var(--color-surface-solid)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div
        className="animate-pulse"
        style={{ height: 'clamp(220px, 30vw, 420px)', background: 'var(--color-surface-muted)' }}
      />
      <div className="px-6 pb-6 pt-4 learner-single:px-8 learner-single:pb-8">
        <div
          className="mb-3 h-5 w-20 animate-pulse rounded-full"
          style={{ background: 'var(--color-surface-muted)' }}
        />
        <div
          className="h-7 w-3/4 animate-pulse rounded-lg"
          style={{ background: 'var(--color-surface-muted)' }}
        />
        <div
          className="mt-2 h-4 w-full animate-pulse rounded"
          style={{ background: 'var(--color-surface-muted)' }}
        />
        <div
          className="mt-1 h-4 w-2/3 animate-pulse rounded"
          style={{ background: 'var(--color-surface-muted)' }}
        />
        <div
          className="mt-5 h-5 w-28 animate-pulse rounded"
          style={{ background: 'var(--color-surface-muted)' }}
        />
      </div>
    </div>
  );
}
